import React from 'react';
import { Paper, Box, Typography, Chip, Button, IconButton, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatPrice, describeEntitlement } from './planUtils';

// One pricing card. `sortable` turns on the drag handle (GA overview);
// the tenant page renders the same card without it. `topFeatures` is the
// list of {feature, entitlement} bullets the caller wants shown.
const PlanCard = ({
  plan, sortable, topFeatures = [], t, mode = 'ga',
  isCurrent, onEdit, onSelect, selectDisabled, selectLabel, taxNote,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: plan._id, disabled: !sortable });
  const style = sortable ? { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 } : undefined;

  return (
    <Paper
      ref={sortable ? setNodeRef : undefined}
      style={style}
      variant="outlined"
      data-testid={`plan-card-${plan.slug}`}
      sx={{
        p: 2, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        borderColor: plan.highlight || isCurrent ? 'primary.main' : 'divider',
        boxShadow: plan.highlight ? (theme) => `inset 0 3px 0 ${theme.palette.primary.main}` : undefined,
        opacity: plan.status === 'archived' ? 0.6 : 1,
      }}
    >
      {sortable && (
        <Tooltip title={t('Drag to reorder', { defaultValue: 'Drag to reorder' })}>
          <IconButton
            size="small"
            aria-label={t('Drag to reorder {{name}}', { name: plan.name, defaultValue: `Drag to reorder ${plan.name}` })}
            sx={{ position: 'absolute', top: 6, right: 6, cursor: 'grab', color: 'text.disabled' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', minHeight: 24, mb: 0.5 }}>
        {plan.visibility === 'private' && <Chip size="small" color="secondary" variant="outlined" label={t('Operator-only', { defaultValue: 'Operator-only' })} />}
        {plan.isDefault && <Chip size="small" color="success" variant="outlined" label={t('Recommended', { defaultValue: 'Recommended' })} />}
        {plan.highlight && <Chip size="small" color="primary" label={t('Most popular', { defaultValue: 'Most popular' })} />}
        {isCurrent && <Chip size="small" color="primary" variant="outlined" label={t('Current plan', { defaultValue: 'Current plan' })} />}
        {plan.status === 'archived' && <Chip size="small" label={t('Archived', { defaultValue: 'Archived' })} />}
        {mode === 'ga' && plan.stripeSynced && <Chip size="small" variant="outlined" label={t('Stripe', { defaultValue: 'Stripe' })} data-testid={`stripe-chip-${plan.slug}`} />}
      </Box>
      <Typography variant="h6">{plan.name}</Typography>
      <Typography variant="h4" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
        {formatPrice(plan.price?.amount, plan.price?.currency)}
        <Typography component="span" variant="body2" color="text.secondary"> / {t('month', { defaultValue: 'month' })}</Typography>
      </Typography>
      {taxNote && (
        <Typography variant="caption" color="text.secondary" data-testid={`tax-note-${plan.slug}`}>{taxNote}</Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, mt: 0.5 }}>{plan.description}</Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1, flex: 1 }}>
        {topFeatures.map(({ feature, entitlement }) => {
          const d = describeEntitlement(feature, entitlement);
          return (
            <Box component="li" key={feature.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{feature.name}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: d.kind === 'no' ? 'text.disabled' : d.kind === 'yes' ? 'success.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                {d.text}{d.kind === 'limit' && feature.kind === 'metered' ? ` / ${t('mo', { defaultValue: 'mo' })}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1 }}>
        {mode === 'ga' ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('{{count}} companies', { count: plan.subscriberCount ?? 0, defaultValue: `${plan.subscriberCount ?? 0} companies` })}
            </Typography>
            <Button size="small" variant="outlined" onClick={onEdit}>{t('Edit', { defaultValue: 'Edit' })}</Button>
          </>
        ) : (
          <Button
            fullWidth
            size="small"
            variant={isCurrent ? 'outlined' : 'contained'}
            disabled={isCurrent || selectDisabled}
            onClick={onSelect}
          >
            {isCurrent ? t('Current plan', { defaultValue: 'Current plan' }) : selectLabel}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default PlanCard;
